import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from './ui/sonner';
import { Pencil, Undo2, History, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };
const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none';

const Input = ({ label, value, onChange, type = 'text', testid, placeholder }) => (
  <label className="block text-xs font-semibold text-slate-600" style={FONT}>{label}
    <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className={`${inputCls} mt-1`} />
  </label>
);

const AddressFields = ({ prefix, value, onChange }) => {
  const v = value || {};
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Input label="Address line 1" value={v.line1} onChange={set('line1')} testid={`${prefix}-line1`} />
      <Input label="Address line 2" value={v.line2} onChange={set('line2')} testid={`${prefix}-line2`} />
      <Input label="City" value={v.city} onChange={set('city')} testid={`${prefix}-city`} />
      <div className="grid grid-cols-2 gap-3">
        <Input label="State" value={v.state} onChange={set('state')} testid={`${prefix}-state`} />
        <Input label="ZIP" value={v.zip_code} onChange={set('zip_code')} testid={`${prefix}-zip`} />
      </div>
    </div>
  );
};

export const ApplicationEditModal = ({ driverId, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API}/drivers/${driverId}/application/editable`, { withCredentials: true })
      .then((r) => { setForm(r.data); setOriginal(r.data); })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load application'));
  }, [driverId]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setError('');
    if (!form.first_name?.trim() || !form.last_name?.trim()) { setError('First and last name are required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || '')) { setError('Enter a valid e-mail address'); return; }
    const emailChanged = (form.email || '').toLowerCase() !== (original.email || '').toLowerCase();
    if (emailChanged && !window.confirm(`Change the sign-in e-mail from ${original.email} to ${form.email}? The employee will be notified at both addresses. Their account, ID, payroll history and contracts are preserved.`)) return;
    if (!window.confirm('Save these corrections to the submitted application? Every change is recorded in the audit trail.')) return;
    setSaving(true);
    try {
      const body = {
        first_name: form.first_name, middle_name: form.middle_name || '', last_name: form.last_name, email: form.email, phone: form.phone || '',
        date_of_birth: form.date_of_birth || '', us_work_authorization: !!form.us_work_authorization,
        residential_address: form.residential_address || {}, mailing_address: form.mailing_address || {}, emergency_contact: form.emergency_contact || {},
        reason: reason || null,
      };
      const r = await axios.put(`${API}/drivers/${driverId}/application`, body, { withCredentials: true });
      toast.success(r.data.changes ? `Application updated (${r.data.changes} change${r.data.changes === 1 ? '' : 's'})` : 'No changes to save');
      onSaved?.();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x) => x.msg).join('; ') : 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4" data-testid="application-edit-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Edit Application</h3>
          <button type="button" onClick={onClose} data-testid="application-edit-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!form ? <div className="p-6 text-sm text-slate-500">{error || 'Loading…'}</div> : (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="First name *" value={form.first_name} onChange={set('first_name')} testid="app-edit-first-name" />
              <Input label="Middle name" value={form.middle_name} onChange={set('middle_name')} testid="app-edit-middle-name" />
              <Input label="Last name *" value={form.last_name} onChange={set('last_name')} testid="app-edit-last-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="E-mail (sign-in) *" type="email" value={form.email} onChange={set('email')} testid="app-edit-email" />
              <Input label="Phone" value={form.phone} onChange={set('phone')} testid="app-edit-phone" />
              <Input label="Date of birth (YYYY-MM-DD)" value={form.date_of_birth} onChange={set('date_of_birth')} testid="app-edit-dob" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700" style={FONT}>
              <input type="checkbox" checked={!!form.us_work_authorization} onChange={(e) => set('us_work_authorization')(e.target.checked)} data-testid="app-edit-work-auth" /> Authorized to work in the US
            </label>
            <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2" style={FONT}>Residential address</p><AddressFields prefix="app-edit-res" value={form.residential_address} onChange={set('residential_address')} /></div>
            <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2" style={FONT}>Mailing address</p><AddressFields prefix="app-edit-mail" value={form.mailing_address} onChange={set('mailing_address')} /></div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2" style={FONT}>Emergency contact</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="Name" value={form.emergency_contact?.name} onChange={(v) => set('emergency_contact')({ ...(form.emergency_contact || {}), name: v })} testid="app-edit-ec-name" />
                <Input label="Relationship" value={form.emergency_contact?.relationship} onChange={(v) => set('emergency_contact')({ ...(form.emergency_contact || {}), relationship: v })} testid="app-edit-ec-relationship" />
                <Input label="Phone" value={form.emergency_contact?.phone} onChange={(v) => set('emergency_contact')({ ...(form.emergency_contact || {}), phone: v })} testid="app-edit-ec-phone" />
              </div>
            </div>
            <Input label="Reason for change (recorded in audit trail)" value={reason} onChange={setReason} testid="app-edit-reason" placeholder="e.g. typo in last name reported by employee" />
            <p className="text-[11px] text-slate-500" style={FONT}>Locked here: SSN, CDL number, banking details, signed agreements, tax forms and finalized payroll — those require their own verification / re-approval flows.</p>
            {error && <p className="text-sm text-red-600" data-testid="app-edit-error">{error}</p>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={onClose} data-testid="app-edit-cancel" className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700">Cancel</button>
              <button type="button" onClick={save} disabled={saving} data-testid="app-edit-save" className="px-4 py-2 text-sm rounded-md bg-[#002FA7] text-white font-semibold disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const RequestCorrectionModal = ({ driverId, onClose, onSaved }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const send = async () => {
    if (note.trim().length < 5) { toast.error('Describe what needs to be corrected'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/drivers/${driverId}/application/request-correction`, { note: note.trim() }, { withCredentials: true });
      toast.success('Application returned to the employee for correction');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Request failed'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="request-correction-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Request employee correction</h3>
        <p className="text-sm text-slate-600" style={FONT}>The employee will be notified and can update the requested information in their application and resubmit it. Their status, account and history are preserved.</p>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} data-testid="request-correction-note" placeholder="Explain what needs to be corrected…" className={inputCls} />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="request-correction-cancel" className="px-4 py-2 text-sm rounded-md border border-slate-300">Cancel</button>
          <button type="button" onClick={send} disabled={saving} data-testid="request-correction-send" className="px-4 py-2 text-sm rounded-md bg-[#002FA7] text-white font-semibold disabled:opacity-50">{saving ? 'Sending…' : 'Send request'}</button>
        </div>
      </div>
    </div>
  );
};

export const ApplicationAdminBar = ({ driverId, profile, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [req, setReq] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);
  const cr = profile?.correction_request;
  const loadAudit = async () => {
    try { const r = await axios.get(`${API}/drivers/${driverId}/application/audit`, { withCredentials: true }); setAudit(r.data); } catch { setAudit([]); }
  };
  useEffect(() => { if (showAudit) loadAudit(); }, [showAudit, profile]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelCr = async () => {
    try { await axios.delete(`${API}/drivers/${driverId}/application/request-correction`, { withCredentials: true }); toast.success('Correction request cancelled'); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const fmt = (v) => (v && typeof v === 'object' ? Object.values(v).filter(Boolean).join(', ') || '—' : (v === null || v === undefined || v === '' ? '—' : String(v)));
  return (
    <div className="mb-6 space-y-3" data-testid="application-admin-bar">
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setEdit(true)} data-testid="edit-application-button" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-[#002FA7] text-white font-semibold" style={FONT}><Pencil className="w-4 h-4" /> Edit Application</button>
        <button type="button" onClick={() => setReq(true)} data-testid="request-correction-button" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-slate-300 text-slate-700" style={FONT}><Undo2 className="w-4 h-4" /> Request Employee Correction</button>
        <button type="button" onClick={() => setShowAudit(!showAudit)} data-testid="application-audit-toggle" className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-slate-300 text-slate-700" style={FONT}><History className="w-4 h-4" /> {showAudit ? 'Hide history' : 'Change history'}</button>
      </div>
      {cr?.status === 'open' && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 flex items-start justify-between gap-3" data-testid="correction-open-banner" style={FONT}>
          <div><strong>Correction requested</strong> by {cr.requested_by} — “{cr.note}”. Waiting for the employee to resubmit.</div>
          <button type="button" onClick={cancelCr} data-testid="correction-cancel-button" className="text-xs underline whitespace-nowrap">Cancel request</button>
        </div>
      )}
      {showAudit && (
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="application-audit-list">
          {audit.length === 0 && <p className="p-4 text-sm text-slate-500" style={FONT}>No changes recorded yet.</p>}
          {audit.map((a) => (
            <div key={a.id} className="p-4 text-sm" style={FONT}>
              <div className="flex flex-wrap justify-between gap-2 text-xs text-slate-500">
                <span><span className="font-semibold text-slate-700">{a.event.replace(/_/g, ' ')}</span> · {a.actor_kind} {a.actor_email}</span>
                <span>{a.at ? new Date(a.at).toLocaleString() : ''}</span>
              </div>
              {a.reason && <p className="text-xs text-slate-600 mt-1">Reason: {a.reason}</p>}
              <ul className="mt-2 space-y-1">
                {a.changes.map((c, i) => (
                  <li key={i} className="text-xs"><span className="font-mono text-slate-700">{c.field}</span>: <span className="line-through text-slate-400">{fmt(c.old)}</span> → <span className="text-slate-900">{fmt(c.new)}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {edit && <ApplicationEditModal driverId={driverId} onClose={() => setEdit(false)} onSaved={onChanged} />}
      {req && <RequestCorrectionModal driverId={driverId} onClose={() => setReq(false)} onSaved={onChanged} />}
    </div>
  );
};
